// Retirement Financial calculation functions

export function calculatePresentValue(
  interestRate,
  numberOfPeriods,
  paymentAmount,
  futureValue = 0,
  paymentType = 0
) {
  if (interestRate === 0)
    return -(futureValue + paymentAmount * numberOfPeriods);
  return -(
    futureValue +
    paymentAmount *
      (1 + interestRate * paymentType) *
      ((1 - Math.pow(1 + interestRate, -numberOfPeriods)) / interestRate)
  );
}

export function calculateFutureValue(
  interestRate,
  numberOfPeriods,
  paymentAmount,
  presentValue = 0,
  paymentType = 0
) {
  if (interestRate === 0)
    return -(presentValue + paymentAmount * numberOfPeriods);
  return -(
    presentValue * Math.pow(1 + interestRate, numberOfPeriods) +
    paymentAmount *
      (1 + interestRate * paymentType) *
      ((Math.pow(1 + interestRate, numberOfPeriods) - 1) / interestRate)
  );
}

export function calculatePeriodicPayment(
  interestRate,
  numberOfPeriods,
  presentValue,
  futureValue = 0,
  paymentType = 0
) {
  if (interestRate === 0)
    return -(presentValue + futureValue) / numberOfPeriods;
  return (
    (-(
      futureValue +
      presentValue * Math.pow(1 + interestRate, numberOfPeriods)
    ) *
      interestRate) /
    ((1 + interestRate * paymentType) *
      (Math.pow(1 + interestRate, numberOfPeriods) - 1))
  );
}

export function calculateInflationAdjustedSum(
  initialAmount,
  inflationRate,
  years
) {
  let total = 0;
  for (let year = 0; year < years; year++) {
    total += initialAmount * Math.pow(1 + inflationRate, year);
  }
  return total;
}

export function formatCurrency(value) {
  return (
    "$" +
    (isNaN(value)
      ? "-"
      : value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }))
  );
}

export const rmdDistributionPeriods = {
  73: 26.5,
  74: 25.5,
  75: 24.6,
  76: 23.7,
  77: 22.9,
  78: 22.0,
  79: 21.1,
  80: 20.2,
  81: 19.4,
  82: 18.5,
  83: 17.7,
  84: 16.8,
  85: 16.0,
  86: 15.2,
  87: 14.4,
  88: 13.7,
  89: 12.9,
  90: 12.2,
  91: 11.5,
  92: 10.8,
  93: 10.1,
  94: 9.5,
  95: 8.9,
  96: 8.4,
  97: 7.8,
  98: 7.3,
  99: 6.8,
  100: 6.4,
};

export const stateIncomeTaxRates = {
  Alabama: 0.05,
  Alaska: 0,
  Arizona: 0.045,
  Arkansas: 0.055,
  California: 0.093,
  Colorado: 0.044,
  Connecticut: 0.065,
  Delaware: 0.066,
  Florida: 0,
  Georgia: 0.0575,
  Hawaii: 0.08,
  Idaho: 0.058,
  Illinois: 0.0495,
  Indiana: 0.0323,
  Iowa: 0.06,
  Kansas: 0.057,
  Kentucky: 0.05,
  Louisiana: 0.0425,
  Maine: 0.0715,
  Maryland: 0.0575,
  Massachusetts: 0.05,
  Michigan: 0.0425,
  Minnesota: 0.0985,
  Mississippi: 0.05,
  Missouri: 0.054,
  Montana: 0.0675,
  Nebraska: 0.0684,
  Nevada: 0,
  "New Hampshire": 0,
  "New Jersey": 0.0897,
  "New Mexico": 0.049,
  "New York": 0.0685,
  "North Carolina": 0.0499,
  "North Dakota": 0.029,
  Ohio: 0.0399,
  Oklahoma: 0.05,
  Oregon: 0.099,
  Pennsylvania: 0.0307,
  "Rhode Island": 0.0599,
  "South Carolina": 0.07,
  "South Dakota": 0,
  Tennessee: 0,
  Texas: 0,
  Utah: 0.0495,
  Vermont: 0.0875,
  Virginia: 0.0575,
  Washington: 0,
  "West Virginia": 0.065,
  Wisconsin: 0.0765,
  Wyoming: 0,
};

export function calculateFederalIncomeTax(preTaxIncome, options = {}) {
  const { filingStatus = "Single", age = 0, spouseAge = 0 } = options;

  // 2024 base standard deductions (IRS)
  const baseStd = {
    Single: 14600,
    "Married Filing Jointly": 29200,
    "Married Filing Separately": 14600,
    "Head of Household": 21900,
    "Qualifying Surviving Spouse": 29200,
  };

  // Additional standard deduction for age 65+ or blindness (2024 amounts)
  // - Single or Head of Household: +$1,950 if 65+ (per individual)
  // - Married (jointly/separately) or Qualifying Surviving Spouse: +$1,550 for each spouse 65+ (per qualifying individual)
  const addForSingleOrHOH = 1950;
  const addForMarriedOrQSS = 1550;

  let standardDeduction = baseStd[filingStatus] || baseStd["Single"];

  if (filingStatus === "Single" || filingStatus === "Head of Household") {
    if (age >= 65) standardDeduction += addForSingleOrHOH;
    // blindness not modeled here; add similarly if you pass a boolean
  } else if (
    filingStatus === "Married Filing Jointly" ||
    filingStatus === "Married Filing Separately" ||
    filingStatus === "Qualifying Surviving Spouse"
  ) {
    // add per qualifying spouse
    if (age >= 65) standardDeduction += addForMarriedOrQSS;
    if (spouseAge >= 65) standardDeduction += addForMarriedOrQSS;
  }

  // Taxable income can't be negative
  const taxableIncome = Math.max(
    0,
    (Number(preTaxIncome) || 0) - standardDeduction
  );

  // 2024 federal tax brackets by filing status (rate applied to the portion in each bracket).
  // Values are the taxable-income thresholds for 2024 (source: IRS / tax publications).
  const brackets = {
    Single: [
      { upTo: 11600, rate: 0.1 },
      { upTo: 47150, rate: 0.12 },
      { upTo: 100525, rate: 0.22 },
      { upTo: 191950, rate: 0.24 },
      { upTo: 243725, rate: 0.32 },
      { upTo: 609350, rate: 0.35 },
      { upTo: Infinity, rate: 0.37 },
    ],
    "Married Filing Jointly": [
      { upTo: 23200, rate: 0.1 },
      { upTo: 94300, rate: 0.12 },
      { upTo: 201050, rate: 0.22 },
      { upTo: 383900, rate: 0.24 },
      { upTo: 487450, rate: 0.32 },
      { upTo: 731200, rate: 0.35 },
      { upTo: Infinity, rate: 0.37 },
    ],
    "Married Filing Separately": [
      { upTo: 11600, rate: 0.1 },
      { upTo: 47150, rate: 0.12 },
      { upTo: 100525, rate: 0.22 },
      { upTo: 191950, rate: 0.24 },
      { upTo: 243725, rate: 0.32 },
      { upTo: 365600, rate: 0.35 },
      { upTo: Infinity, rate: 0.37 },
    ],
    "Head of Household": [
      { upTo: 16550, rate: 0.1 },
      { upTo: 63100, rate: 0.12 },
      { upTo: 100500, rate: 0.22 },
      { upTo: 191950, rate: 0.24 },
      { upTo: 243700, rate: 0.32 },
      { upTo: 609350, rate: 0.35 },
      { upTo: Infinity, rate: 0.37 },
    ],
    "Qualifying Surviving Spouse": [
      // Same thresholds as Married Filing Jointly
      { upTo: 23200, rate: 0.1 },
      { upTo: 94300, rate: 0.12 },
      { upTo: 201050, rate: 0.22 },
      { upTo: 383900, rate: 0.24 },
      { upTo: 487450, rate: 0.32 },
      { upTo: 731200, rate: 0.35 },
      { upTo: Infinity, rate: 0.37 },
    ],
  };

  const selectedBrackets = brackets[filingStatus] || brackets["Single"];

  let tax = 0;
  let lower = 0;
  let remaining = taxableIncome;

  for (let i = 0; i < selectedBrackets.length && remaining > 0; i++) {
    const upper = selectedBrackets[i].upTo;
    const rate = selectedBrackets[i].rate;
    const taxablePortion = Math.max(0, Math.min(remaining, upper - lower));
    tax += taxablePortion * rate;
    remaining -= taxablePortion;
    lower = upper;
  }
  // Defensive: don't return negative
  return Math.max(0, tax);
}

export function allocateSavings(
  totalCommitment,
  iraMaxMonthly = 7000 / 12,
  c401kMaxMonthly = 23000 / 12
) {
  let remaining = totalCommitment;

  // Prioritize Tax-Deferred (401k/IRA) up to the contribution limit
  const deferredSuggestion = Math.min(remaining, c401kMaxMonthly);
  remaining = Math.max(0, remaining - deferredSuggestion);

  // Then allocate to Tax-Free (Roth) up to the contribution limit
  const freeSuggestion = Math.min(remaining, iraMaxMonthly);
  remaining = Math.max(0, remaining - freeSuggestion);

  // Allocate the rest to Taxable (Brokerage)
  const taxableSuggestion = remaining;

  return {
    deferred: deferredSuggestion,
    free: freeSuggestion,
    taxable: taxableSuggestion,
  };
}
